/**
 * pinyin.ts —— 轻量拼音支持（首字母粒度）
 *
 * 为什么不用第三方拼音库：
 *   pinyin-pro 这类库体积在 900KB 以上，而通讯录用到的只有两件事：
 *     ① 按拼音首字母分组 ② 首字母检索
 *   这两件事用一张预生成的查询表就够了，约 30KB，运行时零依赖。
 *
 * 表的构造思路见 scripts/gen-pinyin-table.mjs 与 data/pinyin-table.ts 的注释。
 * 一句话：GB2312 一级字区本身就是拼音序，所以能压成 44 个区间；
 * 二级字区是部首序，只能逐字记录。
 *
 * 边界处理：非汉字字符原样保留（字母转小写），这样
 * "Alice" / "张三" / "123" 混排时也能得到稳定的顺序。
 */

import {
  LEVEL1_ORDER,
  LEVEL1_RANGES,
  LEVEL2_INITIALS_ENCODED,
  SURNAME_OVERRIDES,
} from './pinyin-table';

/** 二级字区映射表。解码一次后复用 —— 3008 条，解析开销可忽略。 */
let level2Map: Map<string, string> | null = null;

function getLevel2Map(): Map<string, string> {
  if (level2Map) return level2Map;
  const map = new Map<string, string>();
  for (const pair of LEVEL2_INITIALS_ENCODED.split(' ')) {
    const idx = pair.indexOf(':');
    if (idx > 0) map.set(pair.slice(0, idx), pair.slice(idx + 1));
  }
  level2Map = map;
  return map;
}

/**
 * 取单个字符的拼音首字母。
 * 查得到返回小写字母；非汉字或生僻字返回原字符本身。
 */
export function charToInitial(ch: string): string {
  // 英文字母直接用
  if (/^[a-zA-Z]$/.test(ch)) return ch.toLowerCase();
  // 数字与符号原样保留
  if (/^[0-9]$/.test(ch)) return ch;

  // ① 一级字区：在拼音序字符串里定位下标，再对区间表做线性查找
  const pos = LEVEL1_ORDER.indexOf(ch);
  if (pos >= 0) {
    for (const [start, end, letter] of LEVEL1_RANGES) {
      if (pos < start) break;
      if (pos <= end) return letter;
    }
  }

  // ② 二级字区：逐字查表
  const sparse = getLevel2Map().get(ch);
  if (sparse) return sparse;

  // ③ 生僻字 / 非汉字：原样返回，分组时归入 '#'
  return ch;
}

/**
 * 把中文名转成可用于排序与检索的拼音键。
 * 例：「王二」→ "we"（用首字母串，够短且能排序）
 *
 * 注意：首字母串无法区分「王二(we)」和「魏恩(we)」，
 * 排序时相同键的元素按输入顺序稳定排列 —— 通讯录场景可以接受。
 */
export function toPinyinKey(name: string): string {
  const chars = Array.from(name.trim());
  return chars
    .map((ch, i) => {
      // 姓的首字应用姓氏修正表（多音字作姓时读音特殊）
      if (i === 0 && SURNAME_OVERRIDES[ch]) return SURNAME_OVERRIDES[ch];
      return charToInitial(ch);
    })
    .join('')
    .toLowerCase();
}

/** 取排序首字母（用于字母索引条）。非字母归到 '#' */
export function firstLetter(name: string): string {
  const first = toPinyinKey(name).charAt(0);
  return /[a-z]/.test(first) ? first.toUpperCase() : '#';
}

/**
 * 匹配判断：关键词是否命中该名字。
 * 依次尝试，任一命中即算匹配：
 *   ① 原文包含（「三」命中「张三」）
 *   ② 拼音键包含（「zs」命中「张三」）
 *   ③ 拼音键以关键词开头 —— 让缩写也能搜
 */
export function matchesKeyword(name: string, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;

  if (name.toLowerCase().includes(kw)) return true;

  const key = toPinyinKey(name);
  return key.includes(kw) || key.startsWith(kw);
}
